import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";
import AnimatedCTA from "../AnimatedCTA";

const TIERS = ["bronze", "silver", "gold", "platinum"];
const MARKET_COUNTS = {
  bronze: 2,
  silver: 2,
  gold: 4,
  platinum: 4,
};

const listKeys = (tier, prefix, count) =>
  Array.from({ length: count }, (_, i) => `accounts.levels.${tier}.${prefix}${i + 1}`);

function BulletGroup({ titleKey, items }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-[12px]">
      <h4 className="font-['Montserrat'] font-bold text-[14px] xl:text-[16px] text-[#ee5414] tracking-[1.32px] uppercase leading-normal">
        {t(titleKey)}
      </h4>
      <ul className="flex flex-col gap-[4px] xl:gap-[6px] list-disc ps-[21px] xl:ps-[24px]">
        {items.map((key) => (
          <li key={key} className="font-['Inter'] font-normal text-[14px] xl:text-[16px] text-[#949494] leading-[1.4]">
            {t(key)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Levels() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);
  const [activeTier, setActiveTier] = useState("bronze");

  return (
    <section className="mt bg-[#020202] py-[80px] xl:py-[160px]">
      <div className="glcontainer flex flex-col gap-[40px] xl:gap-[80px]">

        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-[20px]">
          <h2 className="font-['Montserrat'] font-black text-[24px] xl:text-[46px] text-white tracking-[-0.92px] uppercase leading-none xl:max-w-[504px]">
            {t("accounts.levels.title")}
          </h2>
          <p className="font-['Inter'] font-normal text-[16px] xl:text-[24px] text-[#949494] leading-[1.4] xl:max-w-[602px]">
            {t("accounts.levels.desc")}
          </p>
        </div>

        <div className="flex flex-col gap-[40px] xl:gap-[60px]">

          {/* Tabs */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-[10px] xl:gap-[20px]">
            {TIERS.map((tier) => {
              const isActive = activeTier === tier;
              return (
                <button
                  key={tier}
                  onClick={() => setActiveTier(tier)}
                  className={`h-[40px] xl:h-[60px] rounded-[20px] flex items-center justify-center font-['Montserrat'] font-extrabold text-[14px] xl:text-[17px] tracking-[-0.17px] uppercase leading-normal transition-colors duration-200 cursor-my ${
                    isActive ? "bg-[#1e1e1e] text-[#ee5414]" : "border border-[#6d6d6d] text-[#6d6d6d] hover:text-[#949494] hover:border-[#949494]"
                  }`}
                >
                  {t(`accounts.levels.${tier}.name`)}
                </button>
              );
            })}
          </div>

          {/* Active tier content */}
          <div key={activeTier} className="flex flex-col xl:flex-row gap-[40px] xl:gap-[123px] xl:items-center">

            {/* Left: description + CTA */}
            <div className="flex flex-col gap-[20px] xl:gap-[41px] xl:w-[497px] xl:shrink-0">
              <div className="flex flex-col gap-[30px] xl:gap-[60px]">
                <div className="flex flex-col gap-[20px] xl:gap-[40px]">
                  <div className="flex flex-col gap-[6px] xl:gap-[12px]">
                    <span className="font-['Montserrat'] font-bold text-[14px] xl:text-[16px] text-[#ee5414] tracking-[1.32px] uppercase leading-normal">
                      {t(`accounts.levels.${activeTier}.name`)}
                    </span>
                    <h3 className="font-['Montserrat'] font-black text-[20px] xl:text-[32px] text-white tracking-[-0.76px] uppercase leading-[38px]">
                      {t(`accounts.levels.${activeTier}.headline`)}
                    </h3>
                  </div>
                  <div className="self-start bg-[#1e1e1e] rounded-[100px] flex items-center gap-[12px] px-[14px] py-[5px]">
                    <span className="font-['Inter'] font-semibold text-[10px] xl:text-[12px] text-[#949494] leading-[16px]">
                      {t("accounts.levels.min")}
                    </span>
                    <span className="font-['Montserrat'] font-semibold text-[16px] xl:text-[24px] text-white leading-[32px]">
                      {t(`accounts.levels.${activeTier}.min`)}
                    </span>
                  </div>
                </div>
                <p className="font-['Inter'] font-normal text-[14px] xl:text-[18px] text-[#949494] leading-[1.4]">
                  {t(`accounts.levels.${activeTier}.desc`)}
                </p>
              </div>
              <div className="h-[1px] bg-[rgba(255,255,255,0.12)]" />
              <div className="hidden xl:block">
                <AnimatedCTA href={link}>{t("accounts.banner.cta")}</AnimatedCTA>
              </div>
              <div className="xl:hidden">
                <AnimatedCTA href={link} mobile full>{t("accounts.banner.cta")}</AnimatedCTA>
              </div>
            </div>

            {/* Right: bullet groups */}
            <div className="flex flex-col gap-[20px] xl:gap-[30px] flex-1">
              <BulletGroup titleKey="accounts.levels.market" items={listKeys(activeTier, "m", MARKET_COUNTS[activeTier])} />
              <BulletGroup titleKey="accounts.levels.features" items={listKeys(activeTier, "f", 4)} />
              <BulletGroup titleKey="accounts.levels.best" items={listKeys(activeTier, "b", 3)} />
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}

export default Levels;
